import axios from 'axios';
import { Player, PlayerNote } from '../models/Player';
import { Tournament, TournamentCreateRequest, TournamentPlayer } from '../models/Tournament';
import { API_URL } from '../config';

// Set the base URL for your API
// In development: use the proxy configured in package.json ('/api')
// In production: use the full API URL from environment variables
const API_BASE_URL = process.env.NODE_ENV === 'development' ? '/api' : `${API_URL}/api`;

// Create an axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Player API calls
export const searchPlayers = async (
  name?: string | string[],
  country?: string,
  category?: string,
  gender?: string,
  top: number = 5,
  skip: number = 0
): Promise<Player[]> => {
  const params = new URLSearchParams();

  // Handle name parameter which can be a string or array of strings
  if (Array.isArray(name)) {
    name.forEach(n => params.append('name', n));
  } else if (name) {
    params.append('name', name);
  }

  if (country) params.append('country', country);
  if (category) params.append('category', category);
  if (gender) params.append('gender', gender);
  params.append('top', top.toString());
  params.append('skip', skip.toString());

  const response = await api.get(`/players?${params.toString()}`);
  return response.data;
};

export const getPlayer = async (id: string): Promise<Player> => {
  const response = await api.get(`/players/${id}`);
  return response.data;
};

export const savePlayerNote = async (note: PlayerNote): Promise<PlayerNote> => {
  const response = await api.post('/players/notes', note);
  return response.data;
};

export const getPlayerNote = async (playerId: string, userId: string): Promise<PlayerNote> => {
  const response = await api.get(`/players/${playerId}/notes/${userId}`);
  return response.data;
};

// Tournament API calls
export const getTournaments = async (): Promise<Tournament[]> => {
  const response = await api.get('/tournaments');
  return response.data;
};

export const getTournament = async (id: string): Promise<Tournament> => {
  const response = await api.get(`/tournaments/${id}`);
  return response.data;
};

export const createTournament = async (name: string): Promise<Tournament> => {
  const request: TournamentCreateRequest = { name };
  const response = await api.post('/tournaments', request);
  return response.data;
};

export const addPlayersToTournament = async (tournamentId: string, playerNames: string[]): Promise<TournamentPlayer[]> => {
  try {
    const response = await api.post(`/tournaments/${tournamentId}/players`, { names: playerNames });
    return response.data;
  } catch (error) {
    console.error('Error adding players to tournament:', error);
    throw error;
  }
};

export const removePlayerFromTournament = async (tournamentId: string, playerId: string): Promise<void> => {
  try {
    await api.delete(`/tournaments/${tournamentId}/players/${playerId}`);
  } catch (error) {
    console.error('Error removing player from tournament:', error);
    throw error;
  }
};
